import { useEffect, useRef, useState } from "react";
import { useQuery } from "@tanstack/react-query";
import { useTranslation } from "react-i18next";
import { Bot, ImageUp, Loader2, Plus, Trash2, X } from "lucide-react";

import { Button } from "@/shared/ui/button";
import { ColorPicker } from "@/shared/ui/color-picker";
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle,
} from "@/shared/ui/dialog";
import { Input } from "@/shared/ui/input";
import { Label } from "@/shared/ui/label";
import { Switch } from "@/shared/ui/switch";
import { Textarea } from "@/shared/ui/textarea";
import {
  WEB_ENTRY_LIMITS as L,
  type WebEntryDto,
  type WebEntryUpsert,
} from "../../api/webEntryService";
import { mediaProviderApi } from "@/entities/media-provider";
import { errorToast } from "@/shared/lib/apiErrors";
import { toast } from "@/shared/hooks/use-toast";
import {
  KEFU_WELCOME_QUERY_KEY,
  effectiveWelcome,
  kefuWelcomeService,
} from "../../api/kefuWelcomeService";
import {
  AVATAR_ACCEPT,
  avatarPreviewSrc,
  draftToUpsert,
  toDraft,
  validateAvatarFile,
  validateWebEntryDraft,
  type Draft,
} from "./webEntryDraft";

const THEME_PRESETS = [
  "#2F5BD3",
  "#1677FF",
  "#12A150",
  "#0E9F9A",
  "#F27C1E",
  "#E5484D",
  "#7C3AED",
  "#1F2329",
];

type Props = {
  open: boolean;
  onOpenChange: (v: boolean) => void;
  /** null = 新建 */
  initial: WebEntryDto | null;
  submitting: boolean;
  onSubmit: (body: WebEntryUpsert) => void;
};

export function WebEntryFormDialog({ open, onOpenChange, initial, submitting, onSubmit }: Props) {
  const { t } = useTranslation("platform");
  const k = (s: string) => t(`channelMgmt.webEntry.${s}`);
  const [d, setD] = useState<Draft>(() => toDraft(initial));
  const [error, setError] = useState<string | null>(null);
  // 刚上传的头像临时预览地址(objectKey 是私有桶引用,不能直接当 img src)
  const [uploadedPreview, setUploadedPreview] = useState<string | null>(null);
  const [uploading, setUploading] = useState(false);
  const fileRef = useRef<HTMLInputElement>(null);

  // 欢迎语占位要显示「不填实际会说什么」:租户欢迎语(企微页签设置)→ 默认欢迎语
  const welcomeQ = useQuery({
    queryKey: KEFU_WELCOME_QUERY_KEY,
    queryFn: kefuWelcomeService.get,
    enabled: open,
    retry: false,
    meta: { suppressGlobalErrorToast: true },
  });
  const fallbackWelcome = effectiveWelcome(welcomeQ.data);

  useEffect(() => {
    if (open) {
      setD(toDraft(initial));
      setError(null);
      setUploadedPreview(null);
    }
  }, [open, initial]);

  const onPickAvatar = async (file: File | undefined) => {
    if (fileRef.current) fileRef.current.value = "";
    if (!file) return;
    const err = validateAvatarFile(file);
    if (err) {
      setError(err);
      return;
    }
    setUploading(true);
    try {
      const r = await mediaProviderApi.uploadAsset(file, "logo");
      setD((prev) => ({ ...prev, avatarUrl: r.objectKey }));
      setUploadedPreview(r.tempUrl);
      setError(null);
    } catch (e) {
      toast(errorToast(e, { fallbackTitle: k("form.avatarUploadFailed") }));
    } finally {
      setUploading(false);
    }
  };
  const avatarSrc = avatarPreviewSrc(
    d.avatarUrl,
    uploadedPreview,
    d.avatarUrl && d.avatarUrl === (initial?.avatarUrl ?? "") ? initial?.avatarPreviewUrl : null
  );

  const patch = (p: Partial<Draft>) => setD((prev) => ({ ...prev, ...p }));

  const submit = () => {
    const err = validateWebEntryDraft(d);
    setError(err);
    if (err) return;
    onSubmit(draftToUpsert(d));
  };

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent className="max-w-lg max-h-[90dvh] overflow-y-auto">
        <DialogHeader>
          <DialogTitle>{initial ? k("form.editTitle") : k("form.createTitle")}</DialogTitle>
          <DialogDescription>{k("form.desc")}</DialogDescription>
        </DialogHeader>

        <div className="space-y-4">
          <div className="space-y-1.5">
            <Label htmlFor="we-name">
              {k("form.name")} <span className="text-destructive">*</span>
            </Label>
            <Input
              id="we-name"
              value={d.name}
              maxLength={L.name}
              placeholder={k("form.namePlaceholder")}
              onChange={(e) => patch({ name: e.target.value })}
            />
          </div>
          <div className="space-y-1.5">
            <Label htmlFor="we-agent">{k("form.agentName")}</Label>
            <Input
              id="we-agent"
              value={d.agentName}
              maxLength={L.agentName}
              placeholder={k("form.agentNamePlaceholder")}
              onChange={(e) => patch({ agentName: e.target.value })}
            />
          </div>
          <div className="space-y-1.5">
            <Label htmlFor="we-welcome">{k("form.welcomeText")}</Label>
            <Textarea
              id="we-welcome"
              rows={3}
              value={d.welcomeText}
              maxLength={L.welcomeText}
              placeholder={t("channelMgmt.webEntry.form.welcomePlaceholder", {
                text: fallbackWelcome,
              })}
              onChange={(e) => patch({ welcomeText: e.target.value })}
            />
          </div>

          <div className="space-y-1.5">
            <Label>{k("form.quickQuestions")}</Label>
            <p className="text-xs text-muted-foreground">{k("form.quickHint")}</p>
            <div className="space-y-2">
              {d.quickQuestions.map((q, i) => (
                <div key={i} className="flex items-center gap-2">
                  <Input
                    value={q}
                    maxLength={L.quickQuestion}
                    aria-label={`${k("form.quickQuestions")} ${i + 1}`}
                    placeholder={k("form.quickPlaceholder")}
                    onChange={(e) => {
                      const next = [...d.quickQuestions];
                      next[i] = e.target.value;
                      patch({ quickQuestions: next });
                    }}
                  />
                  <Button
                    type="button"
                    variant="ghost"
                    size="icon"
                    aria-label={k("form.quickRemove")}
                    onClick={() =>
                      patch({ quickQuestions: d.quickQuestions.filter((_, j) => j !== i) })
                    }
                  >
                    <X className="h-4 w-4" />
                  </Button>
                </div>
              ))}
              {d.quickQuestions.length < L.quickQuestions && (
                <Button
                  type="button"
                  variant="outline"
                  size="sm"
                  onClick={() => patch({ quickQuestions: [...d.quickQuestions, ""] })}
                >
                  <Plus className="h-3.5 w-3.5 mr-1" />
                  {k("form.quickAdd")}
                </Button>
              )}
            </div>
          </div>

          <div className="space-y-1.5">
            <Label>{k("form.avatar")}</Label>
            <div className="flex items-center gap-3">
              <div
                className="flex h-12 w-12 shrink-0 items-center justify-center overflow-hidden rounded-full border"
                style={{ backgroundColor: avatarSrc ? undefined : d.themeColor }}
                data-testid="we-avatar-preview"
              >
                {avatarSrc ? (
                  <img
                    src={avatarSrc}
                    alt={k("form.avatar")}
                    className="h-full w-full object-cover"
                  />
                ) : d.avatarUrl ? (
                  <span className="text-2xs text-white">{k("form.avatarSet")}</span>
                ) : (
                  <Bot className="h-6 w-6 text-white" />
                )}
              </div>
              <input
                ref={fileRef}
                type="file"
                accept={AVATAR_ACCEPT}
                className="hidden"
                data-testid="we-avatar-input"
                onChange={(e) => void onPickAvatar(e.target.files?.[0])}
              />
              <Button
                type="button"
                variant="outline"
                size="sm"
                disabled={uploading}
                onClick={() => fileRef.current?.click()}
              >
                {uploading ? (
                  <Loader2 className="h-3.5 w-3.5 mr-1 animate-spin" />
                ) : (
                  <ImageUp className="h-3.5 w-3.5 mr-1" />
                )}
                {d.avatarUrl ? k("form.avatarChange") : k("form.avatarUpload")}
              </Button>
              {d.avatarUrl && (
                <Button
                  type="button"
                  variant="ghost"
                  size="sm"
                  disabled={uploading}
                  data-testid="we-avatar-clear"
                  onClick={() => {
                    patch({ avatarUrl: "" });
                    setUploadedPreview(null);
                  }}
                >
                  <Trash2 className="h-3.5 w-3.5 mr-1" />
                  {k("form.avatarClear")}
                </Button>
              )}
            </div>
            <p className="text-xs text-muted-foreground">{k("form.avatarHint")}</p>
          </div>

          <div className="space-y-1.5">
            <Label>{k("form.themeColor")}</Label>
            <div className="flex items-center gap-2">
              <ColorPicker
                value={d.themeColor}
                onChange={(hex) => patch({ themeColor: hex })}
                presets={THEME_PRESETS}
                ariaLabel={k("form.themeColor")}
              />
              <Input
                className="w-32 font-mono"
                value={d.themeColor}
                maxLength={7}
                aria-label={k("form.themeColorHex")}
                onChange={(e) => patch({ themeColor: e.target.value.trim() })}
              />
            </div>
          </div>

          <div className="flex items-center justify-between gap-3 rounded-md border p-3">
            <div>
              <Label htmlFor="we-lead">{k("form.leadEnabled")}</Label>
              <p className="text-xs text-muted-foreground">{k("form.leadHint")}</p>
            </div>
            <Switch
              id="we-lead"
              checked={d.leadEnabled}
              onCheckedChange={(v) => patch({ leadEnabled: v })}
            />
          </div>
          <div className="flex items-center justify-between gap-3 rounded-md border p-3">
            <div>
              <Label htmlFor="we-enabled">{k("form.enabled")}</Label>
              <p className="text-xs text-muted-foreground">{k("form.enabledHint")}</p>
            </div>
            <Switch
              id="we-enabled"
              checked={d.enabled}
              onCheckedChange={(v) => patch({ enabled: v })}
            />
          </div>

          {error && (
            <p className="text-sm text-destructive" role="alert">
              {k(error)}
            </p>
          )}
        </div>

        <DialogFooter>
          <Button variant="outline" onClick={() => onOpenChange(false)} disabled={submitting}>
            {k("form.cancel")}
          </Button>
          <Button onClick={submit} disabled={submitting || uploading} data-testid="we-form-submit">
            {submitting && <Loader2 className="h-4 w-4 mr-1 animate-spin" />}
            {k("form.save")}
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}
